import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  addressFromFormatted,
  GoogleLocation,
  LocationPreview,
  PlaceAddress,
  PlaceSearch,
  PlaceSuggestion,
  RequestFailure,
  ReviewLink,
  toRequestFailure,
  useGoogleLocation,
  useGoogleLocationQuery
} from '@/features/google-location'
import Alert from '@/shared/components/Alert/Alert'
import Button from '@/shared/components/Button/Button'
import Skeleton from '@/shared/components/Skeleton/Skeleton'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import SettingsBlock from '../components/SettingsBlock'
import { GOOGLE_LOCATION_ANCHOR } from '../store/useSettingsModal'
import styles from '../SettingsModal.module.scss'

const SELECT_ERROR: Record<RequestFailure['kind'], string> = {
  'not-found': 'ALERTS.GOOGLE_LOCATION_SELECT_ERROR.NOT_FOUND',
  // Answered only to a save, never to a pick.
  'not-picked': 'ALERTS.GOOGLE_LOCATION_SELECT_ERROR.DESCRIPTION',
  'rate-limited': 'ALERTS.GOOGLE_LOCATION_SELECT_ERROR.RATE_LIMITED',
  unavailable: 'ALERTS.GOOGLE_LOCATION_SELECT_ERROR.UNAVAILABLE',
  invalid: 'ALERTS.GOOGLE_LOCATION_SELECT_ERROR.DESCRIPTION',
  unknown: 'ALERTS.GOOGLE_LOCATION_SELECT_ERROR.DESCRIPTION'
}

/**
 * A change to the venue's place that Save has not kept yet: a pick, with
 * Google's one-line address for it, or the place let go.
 */
export type GooglePlaceDraft =
  | { kind: 'pick'; location: GoogleLocation; formattedAddress: string }
  | { kind: 'disconnect' }

interface GoogleLocationBlockProps {
  /** Held by the section, so its Save keeps it and leaving drops it. */
  draft: GooglePlaceDraft | null
  /** Null when the change comes back to what is saved. */
  onDraftChange: (draft: GooglePlaceDraft | null) => void
  /** The pick's address, for the section to fill in the venue's with. */
  onAddressFound: (address: PlaceAddress) => void
}

/**
 * The venue's place on Google: found and confirmed here, and the source of
 * the website's map and of the review link and its QR code. Above the
 * address, which a pick fills in.
 *
 * A pick or a disconnect is a draft like the rest of the form: shown here at
 * once, kept by the section's Save together with the address it filled in,
 * and dropped by leaving the section unsaved. Until then the website and the
 * review link keep the saved place. The pick's one request to Google is the
 * only one: Save sends our backend the place it already looked up.
 */
function GoogleLocationBlock({
  draft,
  onDraftChange,
  onAddressFound
}: GoogleLocationBlockProps) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const { location, select } = useGoogleLocation()

  const [isChanging, setIsChanging] = useState(false)
  const [isReselectClosed, setIsReselectClosed] = useState(false)

  // What Save would keep: the draft over the saved place.
  const saved = location.data ?? null
  let current = saved
  if (draft?.kind === 'pick') current = draft.location
  if (draft?.kind === 'disconnect') current = null
  const isActive = current?.status === 'active'

  const pick = (suggestion: PlaceSuggestion, sessionToken: string) =>
    select.mutate(
      { placeId: suggestion.placeId, sessionToken },
      {
        onSuccess: response => {
          onDraftChange({
            kind: 'pick',
            location: response.location,
            formattedAddress: response.formattedAddress
          })
          setIsChanging(false)
          setIsReselectClosed(false)
          // In parts from the backend, or read from the one line where the
          // backend does not send them yet.
          const found =
            response.address ?? addressFromFormatted(response.formattedAddress)
          const isFilled = Object.values(found).some(Boolean)
          if (isFilled) onAddressFound(found)
          showSnackbar(
            t('ALERTS.GOOGLE_LOCATION_PICKED.TITLE'),
            t(
              isFilled
                ? 'ALERTS.GOOGLE_LOCATION_PICKED.ADDRESS_FILLED'
                : 'ALERTS.GOOGLE_LOCATION_PICKED.DESCRIPTION'
            ),
            'success'
          )
        },
        onError: error =>
          showSnackbar(
            t('ALERTS.GOOGLE_LOCATION_SELECT_ERROR.TITLE'),
            t(SELECT_ERROR[toRequestFailure(error).kind]),
            'error'
          )
      }
    )

  const renderLocation = () => {
    // Shaped like the search field, which is what most venues see first.
    if (location.isPending) return <Skeleton height="2.5rem" />
    if (location.isError) {
      return (
        <Alert severity="error" message={t('GOOGLE_LOCATION.LOAD_ERROR')} />
      )
    }

    if (!current || isChanging) {
      return (
        <>
          <PlaceSearch
            onPick={pick}
            isPicking={select.isPending}
            isConnected={!!current}
          />
          {current && (
            <div className={styles.row}>
              <Button
                label={t('COMMON.CANCEL')}
                variant="text"
                color="primary"
                onClick={() => setIsChanging(false)}
              />
            </div>
          )}
        </>
      )
    }

    return (
      <>
        {current.status === 'needs_reselect' && !isReselectClosed && (
          <Alert
            severity="warning"
            message={t('GOOGLE_LOCATION.NEEDS_RESELECT')}
            onClose={() => setIsReselectClosed(true)}
          />
        )}
        {isActive && <LocationPreview embedUrl={current.embedUrl} />}
        {/* Google's address for a pick: answered once and never stored. */}
        {isActive && draft?.kind === 'pick' && (
          <p className={`body-small ${styles.fieldHint}`}>
            {t('GOOGLE_LOCATION.PICKED', { address: draft.formattedAddress })}
          </p>
        )}
        <div className={styles.locationActions}>
          <Button
            label={
              isActive
                ? t('GOOGLE_LOCATION.CHANGE')
                : t('GOOGLE_LOCATION.RESELECT')
            }
            variant="outlined"
            color="primary"
            icon="las la-search-location icon-medium"
            onClick={() => setIsChanging(true)}
          />
          {/*
            No confirmation: like a pick, it waits for Save, and leaving
            without saving undoes it. Letting go of a pick where nothing is
            saved changes nothing.
          */}
          <Button
            label={t('GOOGLE_LOCATION.DISCONNECT')}
            variant="text"
            color="primary"
            onClick={() => onDraftChange(saved ? { kind: 'disconnect' } : null)}
          />
        </div>
      </>
    )
  }

  return (
    <SettingsBlock
      id={GOOGLE_LOCATION_ANCHOR}
      title={t('GOOGLE_LOCATION.LOCATION_TITLE')}
    >
      <div className={styles.googleLocation}>{renderLocation()}</div>
    </SettingsBlock>
  )
}

/**
 * The review link and its QR code, for the saved place. A place Google no
 * longer knows has no review form worth sharing. Kept while a new place is
 * searched for or picked and not yet saved: nobody should print a code for
 * a place that leaving may still drop.
 */
export function GoogleReviewsBlock() {
  const { t } = useTranslation()
  const current = useGoogleLocationQuery().data ?? null

  if (current?.status !== 'active') return null

  return (
    <SettingsBlock title={t('GOOGLE_LOCATION.REVIEWS.TITLE')}>
      <ReviewLink reviewUrl={current.reviewUrl} />
    </SettingsBlock>
  )
}

export default GoogleLocationBlock
